import { useEffect, useRef, useState } from 'react';
import { Camera, Check, ImagePlus, Plus, RotateCcw, Search, X } from 'lucide-react';
import { uid } from '../state';
import { FONT_MONO } from '../theme';
import { Button, Input, NumberInput } from '../ui';

/* Downscale a photo client-side so uploads stay small (max ~1280px, JPEG). */
function downscaleImage(file, maxEdge = 1280, quality = 0.8) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
      const w = Math.max(1, Math.round(img.width * scale));
      const h = Math.max(1, Math.round(img.height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      const dataUrl = canvas.toDataURL('image/jpeg', quality);
      resolve(dataUrl.split(',')[1]); // strip "data:image/jpeg;base64,"
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not read image')); };
    img.src = url;
  });
}

/* Photograph a meal → add an optional note ("4 eggs fried in olive oil") →
 * Claude vision estimates the foods and macros, treating the note as ground
 * truth → user reviews portions and logs everything in one tap. */
/* Two jobs, one sheet:
 *   mode 'meal'  — photograph a plate; the model estimates portions.
 *   mode 'label' — a nutrition label or grocery-app screenshot (Albert Heijn,
 *                  Jumbo, a webshop); the model transcribes the printed table.
 * Label mode drops the `capture` attribute so iOS offers the photo library
 * (a screenshot lives there, not in the camera) and keeps more pixels, because
 * a 6-point nutrition table has to survive the downscale.
 * pendingCode: a barcode the scanner could not resolve. It is stored on the
 * logged food only when exactly one item is logged, so it can't land on the
 * wrong product, and the next scan then resolves from Mo's own foods. */
export function PhotoFoodSheet({ t, token, groupLabel, mode = 'meal', pendingCode = '', onLog, onClose }) {
  const isLabel = mode === 'label';
  const fileRef = useRef(null);
  const [phase, setPhase] = useState('pick'); // pick | context | analyzing | review | error
  const [error, setError] = useState('');
  const [items, setItems] = useState([]); // [{...food item, grams, included}]
  const [photo, setPhoto] = useState(null); // downscaled base64 (no data-URL prefix)
  const [hint, setHint] = useState(''); // user context note sent with the photo

  // Open the camera immediately — on iOS this launches the native camera UI.
  useEffect(() => { fileRef.current?.click(); }, []);

  // Photo taken → downscale and show the context step (photo + note field).
  const onFile = async (file) => {
    if (!file) { if (!photo) onClose(); return; } // camera cancelled with nothing to show
    try {
      const image = isLabel
        ? await downscaleImage(file, 1600, 0.92) // small print needs the detail
        : await downscaleImage(file);
      setPhoto(image);
      setPhase('context');
    } catch {
      setError('Could not read the photo — try again.');
      setPhase('error');
    }
  };

  const analyze = async () => {
    if (!photo) return;
    setPhase('analyzing');
    try {
      const res = await fetch('/api/food/photo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ image: photo, media_type: 'image/jpeg', hint: hint.trim(), mode }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        setError(data.message || 'Analysis failed — try again.');
        setPhase('error');
        return;
      }
      if (!data.items || data.items.length === 0) {
        setError(isLabel
          ? 'No nutrition table found. Make sure the per-100 g values are readable in the shot.'
          : 'No food recognised. Try a clearer shot, or describe the meal in the note.');
        setPhase('error');
        return;
      }
      setItems(data.items.map(it => ({ ...it, grams: Math.round(it.estimated_grams || 100), included: true })));
      setPhase('review');
    } catch {
      setError('Analysis failed — check your connection and try again.');
      setPhase('error');
    }
  };

  const itemMacros = (it) => {
    const g = it.grams || 0;
    const p = it.per100 || {};
    return {
      kcal: (p.kcal || 0) * g / 100, protein: (p.protein || 0) * g / 100,
      carbs: (p.carbs || 0) * g / 100, fats: (p.fats || 0) * g / 100,
    };
  };
  const included = items.filter(it => it.included);
  const totalKcal = included.reduce((s, it) => s + itemMacros(it).kcal, 0);

  const linkCode = pendingCode && included.length === 1 ? pendingCode : '';
  const logAll = () => {
    included.forEach(it => {
      const p = it.per100 || {};
      const unit = it.unit === 'ml' ? 'ml' : 'g';
      onLog({
        id: uid('food'),
        name: it.name, brand: '', unit,
        per: { kcal: (p.kcal || 0) / 100, protein: (p.protein || 0) / 100, carbs: (p.carbs || 0) / 100, fats: (p.fats || 0) / 100 },
        portion: Math.max(1, it.grams || 100),
        serving: `per 100 ${unit}`, source: isLabel ? 'label' : 'photo', code: linkCode,
      });
    });
    onClose();
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: t.cardBg, borderTopLeftRadius: 16, borderTopRightRadius: 16, width: '100%', maxWidth: 560, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 -8px 32px rgba(0,0,0,0.30)' }}>
        <div style={{ padding: '14px 18px', borderBottom: `1px solid ${t.divider}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: t.text, display: 'flex', alignItems: 'center', gap: 8 }}>
            {isLabel ? <ImagePlus size={17} /> : <Camera size={17} />} {isLabel ? 'Label' : 'Photo'} → {groupLabel}
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: t.textFaint, padding: 4, display: 'flex' }}><X size={18} /></button>
        </div>

        {/* Hidden camera input — iOS opens the camera directly */}
        <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }}
          {...(isLabel ? {} : { capture: 'environment' })}
          onChange={e => { onFile(e.target.files?.[0]); e.target.value = ''; }} />

        <div style={{ flex: 1, overflowY: 'auto' }}>
          {phase === 'pick' && (
            <div style={{ padding: '36px 24px', textAlign: 'center' }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12, color: t.textFaint }}>
                {isLabel ? <ImagePlus size={32} /> : <Camera size={32} />}
              </div>
              <div style={{ fontSize: 13, color: t.textMuted, marginBottom: 16, lineHeight: 1.5 }}>
                {isLabel
                  ? 'Pick a screenshot of the product page (Albert Heijn, Jumbo, a webshop) or a photo of the nutrition table. I’ll read the printed values rather than guess them.'
                  : 'Take a photo of your meal and I’ll estimate the foods and macros.'}
              </div>
              <Button t={t} variant="primary" onClick={() => fileRef.current?.click()} style={{ justifyContent: 'center' }}>
                {isLabel ? <><ImagePlus size={15} /> Choose image</> : <><Camera size={15} /> Open camera</>}
              </Button>
            </div>
          )}

          {phase === 'context' && photo && (
            <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <img src={`data:image/jpeg;base64,${photo}`} alt={isLabel ? 'Nutrition label' : 'Your meal'}
                style={{ width: '100%', maxHeight: 240, objectFit: isLabel ? 'contain' : 'cover', borderRadius: 12, border: `1px solid ${t.cardBorder}`, background: isLabel ? t.surfaceAlt : 'transparent' }} />
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: t.textFaint, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 6 }}>
                  Add context (optional)
                </div>
                <Input t={t} autoFocus={false}
                  placeholder={isLabel ? 'e.g. AH magere kwark, ik at 200 g' : 'e.g. 4 eieren gebakken in olijfolie'}
                  value={hint} onChange={e => setHint(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') analyze(); }} />
                <div style={{ fontSize: 11, color: t.textFaint, marginTop: 6, lineHeight: 1.5 }}>
                  {isLabel
                    ? 'Productnaam of hoeveel je ervan at. De macro\u2019s komen van het etiket zelf. Leeg laten mag ook.'
                    : 'Aantallen en bereiding (gebakken, in olie, met saus) maken de schatting veel nauwkeuriger. Leeg laten mag ook.'}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button t={t} onClick={() => fileRef.current?.click()}>
                  {isLabel ? <><ImagePlus size={14} /> Replace</> : <><Camera size={14} /> Retake</>}
                </Button>
                <Button t={t} variant="primary" onClick={analyze} style={{ flex: 1, justifyContent: 'center' }}>
                  <Search size={14} /> Analyze
                </Button>
              </div>
            </div>
          )}

          {phase === 'analyzing' && (
            <div style={{ padding: '48px 24px', textAlign: 'center' }}>
              <div style={{ fontSize: 28, marginBottom: 12 }}>🔎</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: t.text, marginBottom: 6 }}>Analysing your meal…</div>
              <div style={{ fontSize: 12, color: t.textMuted }}>
                {hint.trim() ? `Using your note: “${hint.trim()}”` : 'Identifying foods and estimating portions.'} This takes a few seconds.
              </div>
            </div>
          )}

          {phase === 'error' && (
            <div style={{ padding: '36px 24px', textAlign: 'center' }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: t.danger, marginBottom: 8 }}>Couldn’t analyse the photo</div>
              <div style={{ fontSize: 13, color: t.textMuted, marginBottom: 16 }}>{error}</div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                {photo && (
                  <Button t={t} variant="primary" onClick={() => { setError(''); setPhase('context'); }}>
                    Edit note &amp; retry
                  </Button>
                )}
                <Button t={t} onClick={() => { setError(''); fileRef.current?.click(); }}>
                  <Camera size={14} /> New photo
                </Button>
              </div>
            </div>
          )}

          {phase === 'review' && items.map((it, idx) => {
            const m = itemMacros(it);
            return (
              <div key={idx} style={{
                padding: '12px 18px', borderBottom: `1px solid ${t.divider}`,
                display: 'flex', alignItems: 'center', gap: 12, opacity: it.included ? 1 : 0.45,
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, color: t.text, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {it.name}
                    {it.confidence === 'low' && <span style={{ fontSize: 10, color: t.textFaint }}> · unsure</span>}
                  </div>
                  <div style={{ fontSize: 11, color: t.textFaint, fontFamily: FONT_MONO, marginTop: 2 }}>
                    {Math.round(m.kcal)} kcal · {Math.round(m.protein)}P {Math.round(m.carbs)}C {Math.round(m.fats)}F
                  </div>
                </div>
                <div style={{ width: 84, flexShrink: 0 }}>
                  <NumberInput t={t} value={it.grams} step={10} min={0} suffix={it.unit === 'ml' ? 'ml' : 'g'}
                    onChange={v => setItems(prev => prev.map((x, i) => i === idx ? { ...x, grams: Math.max(0, Number(v) || 0) } : x))} />
                </div>
                <button onClick={() => setItems(prev => prev.map((x, i) => i === idx ? { ...x, included: !x.included } : x))}
                  title={it.included ? 'Exclude' : 'Include'} style={{
                    flexShrink: 0, height: 32, width: 32, borderRadius: 8, cursor: 'pointer',
                    border: `1px solid ${it.included ? t.accent : t.cardBorder}`,
                    background: it.included ? t.accent : 'transparent',
                    color: it.included ? '#fff' : t.textMuted,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                  <Check size={15} />
                </button>
              </div>
            );
          })}
        </div>

        {phase === 'review' && (
          <div style={{ padding: '12px 18px', borderTop: `1px solid ${t.divider}`, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ fontSize: 11, color: t.textFaint }}>
              Estimates — adjust the grams, or refine the note and re-analyse.
            </div>
            {pendingCode && (
              <div style={{ fontSize: 11, color: linkCode ? t.accent : t.textMuted, lineHeight: 1.4 }}>
                {linkCode
                  ? `Barcode ${pendingCode} will be saved with this food.`
                  : `Keep one item to save barcode ${pendingCode} with it.`}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <Input t={t} placeholder="e.g. 4 eieren gebakken in olijfolie"
                value={hint} onChange={e => setHint(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') analyze(); }} />
              <Button t={t} onClick={analyze} style={{ flexShrink: 0 }} title="Re-analyse with this note">
                <RotateCcw size={14} />
              </Button>
            </div>
            <Button t={t} variant="primary" disabled={included.length === 0} onClick={logAll} style={{ justifyContent: 'center' }}>
              <Plus size={14} /> Log {included.length} item{included.length === 1 ? '' : 's'} · {Math.round(totalKcal)} kcal
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
